<template>
  <div
    class="progress-ring"
    :style="{ width: `${size}px`, height: `${size}px` }"
  >
    <svg :width="size" :height="size" :viewBox="`0 0 ${size} ${size}`">
      <circle
        class="progress-ring__track"
        :cx="size / 2"
        :cy="size / 2"
        :r="radius"
        :stroke-width="stroke"
      />
      <circle
        class="progress-ring__fill"
        :cx="size / 2"
        :cy="size / 2"
        :r="radius"
        :stroke-width="stroke"
        :stroke-dasharray="circumference"
        :stroke-dashoffset="dashOffset"
      />
    </svg>
    <div class="progress-ring__content">
      <slot></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    /** Progress percentage, from 0 to 1. */
    progress: number
    size?: number
    stroke?: number
  }>(),
  {
    size: 44,
    stroke: 5,
  },
)

const radius = computed(() => (props.size - props.stroke) / 2)
const circumference = computed(() => 2 * Math.PI * radius.value)
const dashOffset = computed(
  () => circumference.value * (1 - Math.min(1, Math.max(0, props.progress))),
)
</script>

<style lang="scss" scoped>
.progress-ring {
  position: relative;
  flex: none;

  svg {
    display: block;
    transform: rotate(-90deg);
  }

  &__track {
    fill: none;
    stroke: $c-border;
  }

  &__fill {
    fill: none;
    stroke: $c-yellow;
    stroke-linecap: round;
    transition: stroke-dashoffset 0.4s ease;
  }

  &__content {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 500;
    color: $c-black-2;
  }
}

.night-mode .progress-ring {
  &__track {
    stroke: $c-black-7;
  }

  &__content {
    color: $c-dark-4;
  }
}
</style>
